import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';

//comps
import { CircularProgress, Box, TableCell } from '@mui/material';
import GlobalButton from '../../components/global/button';
import DataGrid from '../../components/grid/dataGrid';
import ContentContainer from '../../components/layout/contentContainer';
import { CustomTableCheckboxCell, CustomTableInputCell, CustomTableDateCell } from '../../components/grid/CustomTableCell';

// apis / model
import TermApi from '../../api/term.api';
import { SchoolTerm } from '../../models/term';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { colors } from '../../theme';
import { authenticationService } from '../../services/authenticationService.service';
import { setSchool, setTermSchools } from '../../store/reducers/app';
import { userService } from '../../services/userService.service';

const Terms = () => {
    const dispatch = useAppDispatch();
    const appState = useAppSelector((state) => state.appState);
    const { school } = appState;
    const _termsApi = new TermApi();

    const [terms, setTerms] = useState([]);
    const [resetPage, setResetPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const termSortBy = 'startDate[desc]';

    const loadTermData = async (schoolId: number, sortBy: string) => {
        const termData = await _termsApi.GetTermsBySchoolId(schoolId, sortBy);
        setTerms([...termData]);
        setIsLoading(true);
    };

    useEffect(() => {
        if (school?.id) {
            loadTermData(school.id, termSortBy)
                .catch((error) => toast.error('Error loading terms'))
                .finally(() => setIsLoading(false));
        }
    }, [appState]);

    const addTerm = async (term: SchoolTerm) => {
        let hasEmptyField = false;
        let isValid = true;

        if (term) {
            const { startDate, endDate } = term;
            Object.values(term).find((o) => {
                if (o === '') hasEmptyField = true;
            });
            if (moment(endDate).isBefore(moment(startDate))) {
                isValid = false;
                toast.warning('Term End Date must be before the Start Date.');
            }
        }

        if (hasEmptyField) {
            toast.error('First 4 fields must be filled to add a new term.');

            return hasEmptyField;
        }

        if (!isValid) {
            return true;
        }

        try {
            await _termsApi.AddNewTerm(term);
            toast.success(`Term ${term?.code} successfully created.`);
        } catch (error) {
            toast.error("Error adding term");
        }

        await reloadTermData();
    };

    const saveTerm = async (term: SchoolTerm) => {
        let hasEmptyField = false;

        const { startDate, endDate } = term;
        Object.values(term).find((o) => {
            if (o === '') hasEmptyField = true;
        });

        if (moment(endDate).isBefore(moment(startDate))) {
            toast.warning('Term End Date must be before the Start Date.');
        }

        if (hasEmptyField) {
            toast.error('First 4 fields must be filled to add a new term.');

            return hasEmptyField;
        }

        if (term?.termId === 0) {
            addTerm(term);
        } else {
            try {
                await _termsApi.UpdateTerm(term);
                loadTermData(school.id, termSortBy);
                toast.success(`Changes to term ${term.code} have been successfully saved.`);
            } catch (error) {
                toast.error("Error saving term");
            }
        }

        await reloadTermData();
    };

    const reloadTermData = async () => {
        const userData = await userService.loadUserData();
        userService.saveUserData(userData);
        dispatch(setTermSchools(userData.termSchools));
    };

    const handleCancel = () => {
        const resetTerms = terms.slice(1);
        setTerms(resetTerms);
    };

    const renderRows = (data: any, onChange: any) => {
        const { code, recordCount, isArchived } = data;

        return (
            <>
                <StyledTableCell>{isArchived && <ArchivedLabel>Archived</ArchivedLabel>}</StyledTableCell>
                {data.termId === 0 ? <CustomTableInputCell data={data} name="code" onChange={onChange} sx={{ width: '10%' }} placeholder="Term Code" /> : <StyledTableCell>{code}</StyledTableCell>}
                <CustomTableInputCell data={data} name="label" onChange={onChange} sx={{ width: '10%' }} placeholder="Public Label" />
                <CustomTableDateCell data={data} name="startDate" onChange={onChange} width={'10%'} />
                <CustomTableDateCell data={data} name="endDate" onChange={onChange} width={'10%'} />
                <StyledTableCell sx={{ width: '9%', textAlign: 'center' }}>{recordCount}</StyledTableCell>
                <CustomTableCheckboxCell data={data} name="openForPublic" onChange={onChange} disabled={false} width={'12%'} />
                <CustomTableCheckboxCell data={data} name="openForPortal" onChange={onChange} disabled={false} width={'10%'} />
                <CustomTableCheckboxCell data={data} name="isCalendarOpen" onChange={onChange} disabled={false} width={'12%'} />
                <CustomTableCheckboxCell data={data} name="isExamSchedulesOpen" onChange={onChange} disabled={false} width={'8%'} />
                <CustomTableCheckboxCell data={data} name="showExamsInTimetable" onChange={onChange} disabled={false} width={'8%'} />
            </>
        );
    };

    return (
        <Container>
            <GlobalButton
                variant="contained"
                text="Add Next Term"
                onClick={() => {
                    const isInEdit = terms.find((t) => t.isEditMode);
                    if (!isInEdit) {
                        setTerms([
                            {
                                code: '',
                                endDate: '',
                                gridEndTime: '17:30:00',
                                gridStartTime: '08:30:30',
                                isCalendarOpen: false,
                                isEditMode: true,
                                isExamSchedulesOpen: false,
                                label: '',
                                openForPortal: false,
                                openForPublic: false,
                                recordCount: 0,
                                schoolId: school?.id,
                                startDate: '',
                                termId: 0,
                                termSchoolId: 0,
                            },
                            ...terms,
                        ]);
                    }
                    setResetPage(true);
                }}
            />
            {terms?.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    topHeight={10}
                    columns={termColumns}
                    data={terms}
                    renderRows={renderRows}
                    onSaveAsync={saveTerm}
                    dataItemKey="termId"
                    handleCancel={handleCancel}
                    resetPage={resetPage}
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    && {
        th > a {
            cursor: default;
        }
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;

        & > * {
            margin-right: 10px;
        }
    }
`;

const StyledTableCell = styled(TableCell)`
    && {
        padding: 3px !important;
    }
`;

const ArchivedLabel = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${colors.grey.label};
    border: 1px solid ${colors.grey.labelBorder};
    border-radius: 2px;
    padding: 2px 4px;
    font-size: 11px;
    margin-left: -3px;
    margin-right: 6px;
`;

const termColumns = [
    {
        label: '',
        disabled: true,
    },
    {
        label: 'Term',
        disabled: true,
    },
    {
        label: 'Public Label',
        disabled: true,
    },
    {
        label: 'Start Date',
        disabled: true,
    },
    {
        label: 'End Date',
        disabled: true,
    },
    {
        label: 'Total Records',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        label: 'Open for Public',
        sx: {
            textAlign: 'center',
        },
    },
    {
        label: 'Open for Portal',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        label: 'Calendar Download',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        label: 'Exam Files',
        sx: {
            textAlign: 'center',
        },
        isTooltip: true,
        tooltipText: 'If exam(s) do not appear on the public site, double check if the term is ‘Open for Public.’',
        disabled: true,
    },
    {
        label: 'Exam CRN',
        sx: {
            textAlign: 'center',
        },
        isTooltip: true,
        tooltipText: 'Display exam CRN on the public site',
        disabled: true,
    },
];

export default Terms;
